// Arena player palette (territory-visuals.md §13, backlog C-6; owner decision D-V2: scene colours
// are the measured ones, player and land colours are our own).
//
// - 36 colours: the 24-colour PLAYER_PALETTE minus the 3 that sit closer than ΔE2000 8 to another
//   palette colour (#C8553D ~ #E8453C, #19A0FF ~ #2EA8E6, #FFB000 ~ #E8A33C), plus 15 colours
//   generated BY RULE, never sampled from any reference: hues at golden-angle steps (137.508°)
//   from 17°; "muted" S 35–50 %, V 70–82 % (i < 6, and the even i ≥ 12) or "deep" S 55–75 %,
//   V 45–58 %; at each hue the HSV grid point that keeps the largest ΔE2000 to every colour
//   chosen so far while meeting the rules below. The values are frozen here (the generator is
//   not shipped); the client test re-checks the rules.
// - Rules every colour meets: ΔE2000 ≥ 12 from the ground (ARENA_CLIENT.colors.ground #EBF8F1),
//   ≥ 8 from every other palette colour, and its side shade (RGB × 0.75) ≥ 6 from the ground.
// - Assignment (server, room RNG): a new life takes a random colour no living player uses; it is
//   released on death or exit. Trails are the main colour at α 0.6, so there is no trail colour.
//
// Pure. Owner: lane C (palette), lane S/M assign it.

import { hexToRgb, PLAYER_PALETTE_HEX, type Rgb, rgbChannels } from '../colors.js';

/** PLAYER_PALETTE colours left out of the arena palette (each too close to another one). */
export const ARENA_PALETTE_DROPPED_HEX: readonly string[] = Object.freeze([
  '#C8553D',
  '#19A0FF',
  '#FFB000',
]);

/** The 15 rule-generated additions (see the header). */
export const ARENA_PALETTE_EXTRA_HEX: readonly string[] = Object.freeze([
  '#B38674', '#88D1B2', '#AA74B3', '#A9B374', '#88B0D1', '#B35970', '#1D7320', '#391D73',
  '#73521D', '#2A756F', '#8A2776', '#68913D', '#7481B3', '#73231D', '#69CF8F',
]); // prettier-ignore

/** The 36 arena player colours as hex strings. */
export const ARENA_PALETTE_HEX: readonly string[] = Object.freeze([
  ...PLAYER_PALETTE_HEX.filter((h) => !ARENA_PALETTE_DROPPED_HEX.includes(h)),
  ...ARENA_PALETTE_EXTRA_HEX,
]);

/** The 36 arena player colours as 0xRRGGBB. */
export const ARENA_PALETTE: readonly Rgb[] = Object.freeze(ARENA_PALETTE_HEX.map(hexToRgb));

/**
 * The colour a new arena life takes: a palette colour no living player uses, starting at
 * `seed mod 36` (the room's random pick for the spawn request) and walking the palette from there,
 * so no RNG is drawn here and the arena golden does not depend on the colour rule. With all 36 in
 * use, the seed's own colour.
 */
export function arenaLifeColor(seed: number, inUse: ReadonlySet<Rgb>): Rgb {
  const n = ARENA_PALETTE.length;
  const start = (seed >>> 0) % n;
  for (let i = 0; i < n; i++) {
    const c = ARENA_PALETTE[(start + i) % n] as Rgb;
    if (!inUse.has(c)) return c;
  }
  return ARENA_PALETTE[start] as Rgb;
}

/** sRGB → CIE L*a*b* (D65). */
export function rgbToLab(c: Rgb): [l: number, a: number, b: number] {
  const lin = (v: number): number => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r8, g8, b8] = rgbChannels(c);
  const r = lin(r8);
  const g = lin(g8);
  const b = lin(b8);
  const f = (v: number): number => (v > 0.008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116);
  const x = f((r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / 0.95047);
  const y = f(r * 0.2126729 + g * 0.7151522 + b * 0.072175);
  const z = f((r * 0.0193339 + g * 0.119192 + b * 0.9503041) / 1.08883);
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
}

const DEG = Math.PI / 180;

/** CIEDE2000 colour difference between two sRGB colours (kL = kC = kH = 1). */
export function deltaE2000(c1: Rgb, c2: Rgb): number {
  const [l1, a1, b1] = rgbToLab(c1);
  const [l2, a2, b2] = rgbToLab(c2);
  const cBar = (Math.hypot(a1, b1) + Math.hypot(a2, b2)) / 2;
  const g = 0.5 * (1 - Math.sqrt(cBar ** 7 / (cBar ** 7 + 25 ** 7)));
  const a1p = (1 + g) * a1;
  const a2p = (1 + g) * a2;
  const c1p = Math.hypot(a1p, b1);
  const c2p = Math.hypot(a2p, b2);
  const hue = (b: number, a: number): number => {
    if (b === 0 && a === 0) return 0;
    const v = Math.atan2(b, a) / DEG;
    return v < 0 ? v + 360 : v;
  };
  const h1 = hue(b1, a1p);
  const h2 = hue(b2, a2p);
  const dL = l2 - l1;
  const dC = c2p - c1p;
  let dh = 0;
  if (c1p * c2p !== 0) {
    dh = h2 - h1;
    if (dh > 180) dh -= 360;
    else if (dh < -180) dh += 360;
  }
  const dH = 2 * Math.sqrt(c1p * c2p) * Math.sin((dh * DEG) / 2);
  const lBar = (l1 + l2) / 2;
  const cBarP = (c1p + c2p) / 2;
  let hBar = h1 + h2;
  if (c1p * c2p !== 0) {
    if (Math.abs(h1 - h2) > 180) hBar = h1 + h2 < 360 ? (h1 + h2 + 360) / 2 : (h1 + h2 - 360) / 2;
    else hBar = (h1 + h2) / 2;
  }
  const t =
    1 -
    0.17 * Math.cos((hBar - 30) * DEG) +
    0.24 * Math.cos(2 * hBar * DEG) +
    0.32 * Math.cos((3 * hBar + 6) * DEG) -
    0.2 * Math.cos((4 * hBar - 63) * DEG);
  const dTheta = 30 * Math.exp(-(((hBar - 275) / 25) ** 2));
  const rC = 2 * Math.sqrt(cBarP ** 7 / (cBarP ** 7 + 25 ** 7));
  const sL = 1 + (0.015 * (lBar - 50) ** 2) / Math.sqrt(20 + (lBar - 50) ** 2);
  const sC = 1 + 0.045 * cBarP;
  const sH = 1 + 0.015 * cBarP * t;
  const rT = -Math.sin(2 * dTheta * DEG) * rC;
  return Math.sqrt((dL / sL) ** 2 + (dC / sC) ** 2 + (dH / sH) ** 2 + rT * (dC / sC) * (dH / sH));
}
